import { useMemo, useRef, useState } from 'react';
import type { FormEvent } from 'react';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '~/components/ui/alert-dialog';
import { Avatar, AvatarFallback, AvatarImage } from '~/components/ui/avatar';
import { Badge } from '~/components/ui/badge';
import { Button } from '~/components/ui/button';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from '~/components/ui/dialog';
import { Input } from '~/components/ui/input';
import type { AdminItem } from '~/lib/contracts';

type AdminItemsPanelProps = {
    items: AdminItem[];
    caseNamesByItemId: Record<number, string[]>;
    isBusy: boolean;
    onCreate: (payload: { name: string }) => Promise<AdminItem>;
    onUpdate: (itemId: number, payload: { name?: string; imageUrl?: string | null }) => Promise<void>;
    onDelete: (itemId: number) => Promise<void>;
    onUploadImage: (itemId: number, file: File) => Promise<void>;
};

export function AdminItemsPanel({
    items,
    caseNamesByItemId,
    isBusy,
    onCreate,
    onUpdate,
    onDelete,
    onUploadImage,
}: AdminItemsPanelProps) {
    const fileInputByItemId = useRef<Record<number, HTMLInputElement | null>>({});
    const createFileInputRef = useRef<HTMLInputElement | null>(null);
    const [newName, setNewName] = useState('');
    const [newImageFile, setNewImageFile] = useState<File | null>(null);
    const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
    const [draftNameById, setDraftNameById] = useState<Record<number, string>>({});
    const [imageDeleteCandidate, setImageDeleteCandidate] = useState<AdminItem | null>(null);

    const sortedItems = useMemo(() => {
        return [...items].sort((a, b) => a.name.localeCompare(b.name));
    }, [items]);

    const handleCreate = async (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        const name = newName.trim();
        if (!name) {
            return;
        }

        const createdItem = await onCreate({ name });
        if (newImageFile) {
            await onUploadImage(createdItem.id, newImageFile);
        }

        setNewName('');
        setNewImageFile(null);
        if (createFileInputRef.current) {
            createFileInputRef.current.value = '';
        }
        setIsCreateDialogOpen(false);
    };

    const handleAvatarClick = (item: AdminItem) => {
        if (item.imageUrl) {
            setImageDeleteCandidate(item);
            return;
        }

        fileInputByItemId.current[item.id]?.click();
    };

    const resolveNameValue = (item: AdminItem) => {
        return draftNameById[item.id] ?? item.name;
    };

    const getItemInitials = (name: string) => {
        const firstWord = name.trim().split(/\s+/).at(0);
        return firstWord?.slice(0, 2).toUpperCase() ?? 'IT';
    };

    return (
        <section className="rounded-xl border border-orange-500/25 bg-linear-to-br from-zinc-950 to-zinc-900 p-4 shadow-2xl">
            <div className="flex items-center justify-between gap-3">
                <h2 className="text-lg font-bold">Items</h2>
                <span className="rounded-full border border-white/20 px-2.5 py-1 text-xs text-white/85">
                    {items.length}
                </span>
            </div>
            <div className="mt-4">
                <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
                    <DialogTrigger asChild>
                        <Button disabled={isBusy}>Create new item</Button>
                    </DialogTrigger>
                    <DialogContent className="sm:max-w-md">
                        <DialogHeader>
                            <DialogTitle>Create item</DialogTitle>
                            <DialogDescription>Add a new item and upload its avatar image.</DialogDescription>
                        </DialogHeader>
                        <form className="space-y-3" onSubmit={(event) => void handleCreate(event)}>
                            <Input
                                value={newName}
                                placeholder="Item name"
                                onChange={(event) => {
                                    setNewName(event.currentTarget.value);
                                }}
                            />
                            <Input
                                ref={createFileInputRef}
                                type="file"
                                accept=".jpg,.jpeg,.png,.webp,.gif,image/jpeg,image/png,image/webp,image/gif"
                                onChange={(event) => {
                                    const nextFile = event.currentTarget.files?.[0] ?? null;
                                    setNewImageFile(nextFile);
                                }}
                            />
                            <DialogFooter>
                                <Button type="submit" disabled={isBusy}>
                                    Create item
                                </Button>
                            </DialogFooter>
                        </form>
                    </DialogContent>
                </Dialog>
            </div>

            <div className="mt-4 space-y-2">
                {sortedItems.map((item) => {
                    const itemName = resolveNameValue(item);
                    const itemCaseNames = caseNamesByItemId[item.id] ?? [];
                    const isNameChanged = itemName.trim() !== item.name;
                    const nextName = itemName.trim();

                    return (
                        <article
                            key={item.id}
                            className="flex justify-between items-center gap-3 rounded-xl border border-white/10 bg-white/2 p-3 "
                        >
                            <div className="flex items-center gap-3">
                                <button
                                    type="button"
                                    className="cursor-pointer border-0 bg-transparent p-0 disabled:cursor-not-allowed disabled:opacity-50"
                                    disabled={isBusy}
                                    onClick={() => {
                                        handleAvatarClick(item);
                                    }}
                                >
                                    <Avatar size="lg">
                                        <AvatarImage src={item.imageUrl ?? undefined} alt={item.name} />
                                        <AvatarFallback>{getItemInitials(item.name)}</AvatarFallback>
                                    </Avatar>
                                </button>
                                <input
                                    ref={(element) => {
                                        fileInputByItemId.current[item.id] = element;
                                    }}
                                    className="hidden"
                                    type="file"
                                    accept=".jpg,.jpeg,.png,.webp,.gif,image/jpeg,image/png,image/webp,image/gif"
                                    onChange={(event) => {
                                        const nextFile = event.currentTarget.files?.[0];
                                        if (!nextFile) {
                                            return;
                                        }

                                        void onUploadImage(item.id, nextFile);
                                        event.currentTarget.value = '';
                                    }}
                                />
                                <Input
                                    value={itemName}
                                    disabled={isBusy}
                                    className="w-96"
                                    onChange={(event) => {
                                        setDraftNameById((current) => ({
                                            ...current,
                                            [item.id]: event.currentTarget.value,
                                        }));
                                    }}
                                    onBlur={() => {
                                        if (!isNameChanged || !nextName) {
                                            return;
                                        }

                                        void onUpdate(item.id, { name: nextName });
                                    }}
                                />
                            </div>
                            <div className="flex justify-between items-center gap-3">
                                <div className="flex min-h-6 flex-wrap items-center gap-1">
                                    {itemCaseNames.map((caseName) => (
                                        <Badge key={`${item.id}-${caseName}`} variant="secondary">
                                            {caseName}
                                        </Badge>
                                    ))}
                                    {itemCaseNames.length === 0 && (
                                        <span className="text-sm text-white/60">No cases assigned</span>
                                    )}
                                </div>
                                <div className="flex items-center gap-2">
                                    <Button
                                        variant="destructive"
                                        disabled={isBusy}
                                        onClick={() => void onDelete(item.id)}
                                    >
                                        Delete
                                    </Button>
                                </div>
                            </div>
                        </article>
                    );
                })}
                {sortedItems.length === 0 && <p className="text-sm text-white/60">No items yet.</p>}
            </div>
            <AlertDialog
                open={imageDeleteCandidate !== null}
                onOpenChange={(isOpen) => {
                    if (isOpen) {
                        return;
                    }

                    setImageDeleteCandidate(null);
                }}
            >
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Delete avatar?</AlertDialogTitle>
                        <AlertDialogDescription>
                            This will remove the avatar image from the item.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel disabled={isBusy}>Cancel</AlertDialogCancel>
                        <AlertDialogAction
                            variant="destructive"
                            disabled={isBusy || !imageDeleteCandidate}
                            onClick={() => {
                                if (!imageDeleteCandidate) {
                                    return;
                                }

                                void onUpdate(imageDeleteCandidate.id, { imageUrl: null });
                                setImageDeleteCandidate(null);
                            }}
                        >
                            Delete avatar
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </section>
    );
}
